import React, { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { themeColors } from '../styles/theme';
import Fonts from './Fonts';
import { useNavigate } from 'react-router-dom';
import config from '../config';

// Styled components
const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '400px',
  width: '100%',
  borderRadius: '16px',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
}));

const LoginButton = styled(Button)(({ theme }) => ({
  background: themeColors.orange,
  color: '#fff',
  padding: '12px 24px',
  fontSize: '1rem',
  marginTop: theme.spacing(3),
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 500,
  boxShadow: '0 4px 10px rgba(252, 82, 8, 0.3)',
  '&:hover': {
    background: `linear-gradient(90deg, ${themeColors.orange} 0%, ${themeColors.mainYellow} 100%)`,
    boxShadow: '0 6px 14px rgba(252, 82, 8, 0.4)',
  },
}));

const AdminLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await axios.post(`${config.apiBaseUrl}api-token-auth/`, {
        username,
        password
      });

      if (response.data.token) {
        // Save the token in localStorage
        localStorage.setItem('admin_token', response.data.token);
        // Redirect to the admin moderation page
        navigate('/admin/moderation');
      } else {
        setError('Invalid response from server');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 3,
      }}
    >
      <Fonts />
      <Typography variant="h3" component="h1" gutterBottom sx={{ 
        fontWeight: 700, 
        color: themeColors.darkBackground,
        marginBottom: 4
      }}>
        Admin Login
      </Typography>

      <LoginPaper>
        <Typography variant="h5" sx={{ marginBottom: 3, fontWeight: 600 }}>
          Sign In
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin} style={{ width: '100%' }}>
          <TextField
            label="Username"
            variant="outlined"
            fullWidth
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={loading}
            required
          />
          <TextField
            label="Password"
            type="password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            required
          />
          <LoginButton
            type="submit"
            fullWidth
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Logging in...' : 'Login'}
          </LoginButton>
        </form>
      </LoginPaper>
    </Box>
  );
};

export default AdminLogin; 